import React from 'react'
import { Card, CardContent, CardHeader, Label, Switch, Button } from '@/shared/ui'
import { usePushSubscription } from '../hooks/usePushSubscription'
import { useToast } from '@/shared/ui/Toast'
import { ensureSwActiveAndControlled } from '../services/webpushClient'
import { getPlatformInfo } from '../utils/platform'

export default function PushSettingsCard() {
  const { status, loading, pwaInstalled, enable, disable } = usePushSubscription()
  const [saving, setSaving] = React.useState(false)
  const { add: toast } = useToast()
  const platform = React.useMemo(() => getPlatformInfo(), [])

  const checked = status === 'subscribed'
  const hasNotifications = typeof Notification !== 'undefined'
  const hasSw = typeof navigator !== 'undefined' && 'serviceWorker' in navigator
  const isBlocked = hasNotifications && Notification.permission === 'denied'
  const unsupported = !hasNotifications || !hasSw
  const requiresInstall = platform.isIos
  const disabled = loading || saving || isBlocked || unsupported || (requiresInstall && !pwaInstalled)

  async function onToggle(next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      if (next) {
        const ok = await enable()
        if (!ok) throw new Error('Falha ao ativar push')
      } else {
        await disable()
      }
      toast({ title: 'Notificações Push', description: next ? 'Ativadas neste dispositivo' : 'Desativadas', variant: 'success', soft: true })
    } catch (e:any) {
      toast({ title: 'Notificações Push', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally { setSaving(false) }
  }

  async function onDiagnose() {
    if (saving) return
    setSaving(true)
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        const p = await Notification.requestPermission()
        if (p !== 'granted') {
          toast({ title: 'Notificações', description: 'Permissão negada no sistema.', variant: 'error' })
          return
        }
      }
      const sw = await ensureSwActiveAndControlled()
      if (sw === 'reload_needed') {
        toast({ title: 'Notificações', description: 'Precisamos recarregar o app para concluir a atualização.', variant: 'error' })
        return
      }
      const ok = await enable()
      toast({ title: 'Notificações', description: ok ? 'Reativadas com sucesso' : 'Falha ao reativar', variant: ok ? 'success' : 'error', soft: true })
    } catch (e:any) {
      toast({ title: 'Notificações', description: e?.message || 'Falha ao reativar', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="text-sm font-medium">Notificações Push (PWA)</CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
          <div>
            <div className="flex items-center gap-2">
              <Label>Desativado</Label>
              <Switch checked={checked} onChange={(e)=> onToggle((e.target as HTMLInputElement).checked)} disabled={disabled} />
              <Label>Ativado</Label>
            </div>
            <div className="mt-2 text-sm text-foreground/80">Receba alertas de Oportunidades Confirmadas.</div>
            <div className="text-xs text-foreground/70">Requer permissao de notificacoes. No iOS, somente no app instalado.</div>
            {unsupported && (
              <div className="text-xs text-foreground/70">Este navegador nao suporta notificacoes push.</div>
            )}
          </div>
          <div className="md:flex md:items-start md:justify-end gap-2">
            <Button variant="outline" className="h-11 w-full md:w-auto" onClick={()=> location.reload()}>Recarregar app</Button>
            <Button className="h-11 w-full md:w-auto" onClick={onDiagnose} disabled={disabled}>Diagnosticar e reativar</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
